import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, User, Activity, Mic, FileText, Camera, Edit3, Plus, 
  TrendingUp, TrendingDown, ChevronDown, Play, Square, Loader2,
  Calendar, Scale, Ruler, Target, CheckCircle2, AlertCircle, X,
  Upload, Trash2, Image
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { clientsAPI, measurementsAPI, consultationsAPI, feedbackAPI, photosAPI } from '../api';

const ClientDetail = ({ client, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [clientData, setClientData] = useState(client);
  const [measurements, setMeasurements] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientData();
  }, [client.id]);

  const loadClientData = async () => {
    try {
      const [measurementsRes, consultationsRes, trendsRes] = await Promise.all([
        measurementsAPI.list(client.id),
        consultationsAPI.list(client.id),
        measurementsAPI.getTrends(client.id),
      ]);
      setMeasurements(measurementsRes.data);
      setConsultations(consultationsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: User },
    { id: 'measurements', label: 'Mediciones', icon: Activity },
    { id: 'consultations', label: 'Consultas AI', icon: Mic },
    { id: 'photos', label: 'Fotos', icon: Camera },
  ];

  const latestMeasurement = measurements[0];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E6E1] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-to-dashboard"
                onClick={onBack}
                className="p-2 rounded-xl hover:bg-stone-100 transition-colors"
              >
                <ArrowLeft size={22} className="text-stone-600" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white font-semibold text-xl">
                  {clientData.name.charAt(0)}
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {clientData.name}
                  </h1>
                  <p className="text-stone-500">
                    {clientData.gender === 'HOMBRE' ? 'Masculino' : 'Femenino'} • {clientData.age} años
                    {clientData.height_cm && ` • ${clientData.height_cm} cm`}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button className="btn-secondary text-sm">
                <Edit3 size={16} className="mr-2" />
                Editar
              </button>
              <button 
                data-testid="new-measurement-btn"
                className="btn-primary text-sm flex items-center"
                onClick={() => setActiveTab('measurements')}
              >
                <Plus size={16} className="mr-2" />
                Nueva medición
              </button>
            </div>
          </div>

          {/* Tabs */}
          <nav className="flex gap-1 mt-6 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-t-xl transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-[#6B8E7D] text-[#6B8E7D] bg-[#6B8E7D]/5'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab 
                client={clientData} 
                measurements={measurements} 
                consultations={consultations}
                trends={trends}
              />
            )}
            {activeTab === 'measurements' && (
              <MeasurementsTab 
                client={clientData}
                measurements={measurements}
                trends={trends}
                onMeasurementCreated={(m) => setMeasurements([m, ...measurements])}
              />
            )}
            {activeTab === 'consultations' && (
              <ConsultationsTab 
                client={clientData}
                consultations={consultations}
                onConsultationCreated={(c) => setConsultations([c, ...consultations])}
              />
            )}
            {activeTab === 'photos' && (
              <PhotosTab client={clientData} measurements={measurements} />
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ client, measurements, consultations, trends }) => {
  const latest = measurements[0];
  const previous = measurements[1];
  
  const weightChange = latest && previous 
    ? (latest.weight_kg - previous.weight_kg).toFixed(1) 
    : null;

  const bodyFatChange = latest && previous && latest.calculated_body_fat?.average && previous.calculated_body_fat?.average
    ? (latest.calculated_body_fat.average - previous.calculated_body_fat.average).toFixed(1)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
      {/* Stats Cards */}
      <div className="lg:col-span-2 grid grid-cols-2 gap-4">
        <div className="card-organic">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-[#6B8E7D]/10">
              <Scale size={20} className="text-[#6B8E7D]" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Peso actual</span>
          </div>
          <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {latest?.weight_kg || '—'} <span className="text-lg text-stone-400">kg</span>
          </p>
          {weightChange && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${parseFloat(weightChange) < 0 ? 'text-[#6B8E7D]' : 'text-[#C25934]'}`}>
              {parseFloat(weightChange) < 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
              {weightChange} kg desde última medición
            </p>
          )}
        </div>

        <div className="card-organic">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-[#C25934]/10">
              <Activity size={20} className="text-[#C25934]" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Grasa corporal</span>
          </div>
          <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {latest?.calculated_body_fat?.average?.toFixed(1) || '—'} <span className="text-lg text-stone-400">%</span>
          </p>
          {bodyFatChange && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${parseFloat(bodyFatChange) < 0 ? 'text-[#6B8E7D]' : 'text-[#C25934]'}`}>
              {parseFloat(bodyFatChange) < 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
              {bodyFatChange}% desde última medición
            </p>
          )}
        </div>

        <div className="card-organic">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-stone-100">
              <Calendar size={20} className="text-stone-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Mediciones</span>
          </div>
          <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {measurements.length}
          </p>
          <p className="text-sm text-stone-500 mt-2">
            Última: {latest ? new Date(latest.measurement_date).toLocaleDateString('es-ES') : 'Sin mediciones'}
          </p>
        </div>

        <div className="card-organic">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-[#8AA894]/20">
              <Mic size={20} className="text-[#6B8E7D]" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Consultas AI</span>
          </div>
          <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {consultations.length}
          </p>
          <p className="text-sm text-stone-500 mt-2">
            Última: {consultations[0] ? new Date(consultations[0].session_date).toLocaleDateString('es-ES') : 'Sin consultas'}
          </p>
        </div>
      </div>

      {/* Latest Consultation Summary */}
      <div className="card-organic">
        <h3 className="text-lg font-medium text-stone-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Última consulta
        </h3>
        {consultations[0] ? (
          <div>
            <p className="text-sm text-stone-600 leading-relaxed mb-4">
              {consultations[0].ai_summary || 'Sin resumen disponible'}
            </p>
            {consultations[0].action_items?.length > 0 && (
              <div className="border-t border-stone-100 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">
                  Acciones a seguir
                </p>
                <ul className="space-y-2">
                  {consultations[0].action_items.slice(0, 3).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <CheckCircle2 size={16} className="text-[#6B8E7D] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {consultations[0].mood && (
              <div className="mt-4 pt-4 border-t border-stone-100">
                <span className={`badge-${consultations[0].mood}`}>
                  {consultations[0].mood === 'positive' ? 'Estado positivo' : 
                   consultations[0].mood === 'neutral' ? 'Estado neutral' : 'Requiere atención'}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Mic size={32} className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-500">Sin consultas registradas</p>
          </div>
        )}
      </div>

      {/* Weight Trend Chart */}
      {trends?.weight_trend?.length > 1 && (
        <div className="lg:col-span-2 card-organic">
          <h3 className="text-lg font-medium text-stone-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Evolución del peso
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends.weight_trend}>
                <defs>
                  <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B8E7D" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#6B8E7D" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="date" tick={{ fill: '#57605B', fontSize: 12 }} />
                <YAxis tick={{ fill: '#57605B', fontSize: 12 }} domain={['auto', 'auto']} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E8E6E1',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#6B8E7D" 
                  strokeWidth={2}
                  fill="url(#weightGradient)" 
                  name="Peso (kg)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Body Fat Chart */}
      {trends?.body_fat_trend?.length > 1 && (
        <div className="card-organic">
          <h3 className="text-lg font-medium text-stone-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Grasa corporal
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends.body_fat_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="date" tick={{ fill: '#57605B', fontSize: 12 }} />
                <YAxis tick={{ fill: '#57605B', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E8E6E1',
                    borderRadius: '12px'
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="average" stroke="#C25934" strokeWidth={2} name="Promedio" dot={{ fill: '#C25934' }} />
                <Line type="monotone" dataKey="jp7" stroke="#6B8E7D" strokeWidth={1} strokeDasharray="3 3" name="JP7" />
                <Line type="monotone" dataKey="jp4" stroke="#8AA894" strokeWidth={1} strokeDasharray="3 3" name="JP4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// Measurements Tab
const MeasurementsTab = ({ client, measurements, trends, onMeasurementCreated }) => {
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-medium text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Historial de mediciones
        </h2>
        <button
          data-testid="add-measurement-btn"
          onClick={() => setShowForm(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Nueva medición
        </button>
      </div>

      {measurements.length === 0 ? (
        <div className="card-organic empty-state">
          <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
            <Activity size={28} className="text-stone-400" />
          </div>
          <h4 className="text-lg font-medium text-stone-700 mb-2">Sin mediciones</h4>
          <p className="text-stone-500 mb-6">Registra la primera medición de este cliente</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Agregar medición
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {measurements.map((measurement, index) => (
            <MeasurementCard key={measurement.id} measurement={measurement} index={index} />
          ))}
        </div>
      )}

      {showForm && (
        <MeasurementForm
          client={client}
          onClose={() => setShowForm(false)}
          onCreated={(m) => {
            onMeasurementCreated(m);
            setShowForm(false);
          }}
        />
      )}
    </div>
  );
};

// Measurement Card
const MeasurementCard = ({ measurement, index }) => {
  const [expanded, setExpanded] = useState(false);
  const bf = measurement.calculated_body_fat;

  return (
    <div className={`card-organic animate-fade-in stagger-${index % 4 + 1}`}>
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-2xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {new Date(measurement.measurement_date).getDate()}
            </p>
            <p className="text-xs text-stone-500 uppercase">
              {new Date(measurement.measurement_date).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' })}
            </p>
          </div>
          <div className="w-px h-12 bg-stone-200" />
          <div className="flex gap-8">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Peso</p>
              <p className="text-lg font-medium text-stone-800">{measurement.weight_kg} kg</p>
            </div>
            {bf?.average && (
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Grasa</p>
                <p className="text-lg font-medium text-stone-800">{bf.average.toFixed(1)}%</p>
              </div>
            )}
            {measurement.skinfold_sum && (
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Suma pliegues</p>
                <p className="text-lg font-medium text-stone-800">{measurement.skinfold_sum} mm</p>
              </div>
            )}
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`} 
        />
      </div>

      {expanded && (
        <div className="mt-6 pt-6 border-t border-stone-100 grid grid-cols-2 md:grid-cols-4 gap-6 animate-fade-in">
          {/* Body Fat Details */}
          {bf && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Fórmulas de grasa corporal</p>
              <div className="grid grid-cols-4 gap-4">
                {bf.jp7 && <div className="text-center p-3 bg-stone-50 rounded-xl"><p className="text-xs text-stone-500">JP7</p><p className="font-medium">{bf.jp7.toFixed(1)}%</p></div>}
                {bf.jp4 && <div className="text-center p-3 bg-stone-50 rounded-xl"><p className="text-xs text-stone-500">JP4</p><p className="font-medium">{bf.jp4.toFixed(1)}%</p></div>}
                {bf.yuhasz && <div className="text-center p-3 bg-stone-50 rounded-xl"><p className="text-xs text-stone-500">Yuhasz</p><p className="font-medium">{bf.yuhasz.toFixed(1)}%</p></div>}
                {bf.dw && <div className="text-center p-3 bg-stone-50 rounded-xl"><p className="text-xs text-stone-500">D-W</p><p className="font-medium">{bf.dw.toFixed(1)}%</p></div>}
              </div>
            </div>
          )}

          {/* Perimeters */}
          {measurement.perimeters && Object.keys(measurement.perimeters).some(k => measurement.perimeters[k]) && (
            <div className="col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Perímetros (cm)</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(measurement.perimeters).filter(([_, v]) => v).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-stone-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skinfolds */}
          {measurement.skinfolds && Object.keys(measurement.skinfolds).some(k => measurement.skinfolds[k]) && (
            <div className="col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Pliegues (mm)</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(measurement.skinfolds).filter(([_, v]) => v).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-stone-500 capitalize">{key}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {measurement.notes && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Notas</p>
              <p className="text-sm text-stone-600">{measurement.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Measurement Form
const MeasurementForm = ({ client, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('basic');
  const [formData, setFormData] = useState({
    weight_kg: '',
    notes: '',
    perimeters: {},
    skinfolds: {},
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await measurementsAPI.create(client.id, {
        weight_kg: parseFloat(formData.weight_kg),
        weight_unit: 'kg',
        perimeters: formData.perimeters,
        skinfolds: formData.skinfolds,
        notes: formData.notes || null,
      });
      onCreated(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la medición');
    } finally {
      setLoading(false);
    }
  };

  const perimeterFields = [
    { key: 'brazoDerRelajado', label: 'Brazo der. relajado' },
    { key: 'brazoDerApretado', label: 'Brazo der. contraído' },
    { key: 'hombros', label: 'Hombros' },
    { key: 'pecho', label: 'Pecho' },
    { key: 'cintura', label: 'Cintura' },
    { key: 'abdomen', label: 'Abdomen' },
    { key: 'gluteo', label: 'Glúteo' },
    { key: 'musloAlto', label: 'Muslo alto' },
    { key: 'musloMedio', label: 'Muslo medio' },
    { key: 'gemeloDerecho', label: 'Gemelo der.' },
  ];

  const skinfoldFields = [
    { key: 'triceps', label: 'Tríceps' },
    { key: 'biceps', label: 'Bíceps' },
    { key: 'subescapular', label: 'Subescapular' },
    { key: 'suprailiaco', label: 'Suprailíaco' },
    { key: 'supraespinal', label: 'Supraespinal' },
    { key: 'abdominal', label: 'Abdominal' },
    { key: 'cuadriceps', label: 'Cuádriceps' },
    { key: 'gemelo', label: 'Gemelo' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 animate-fade-in" data-testid="measurement-form-modal">
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <h3 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nueva medición
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
            <X size={20} className="text-stone-500" />
          </button>
        </div>

        <div className="flex border-b border-stone-100">
          {['basic', 'perimeters', 'skinfolds'].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeSection === section
                  ? 'text-[#6B8E7D] border-b-2 border-[#6B8E7D]'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              {section === 'basic' ? 'Básico' : section === 'perimeters' ? 'Perímetros' : 'Pliegues'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {activeSection === 'basic' && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                  Peso (kg) *
                </label>
                <input
                  data-testid="measurement-weight-input"
                  type="number"
                  step="0.1"
                  value={formData.weight_kg}
                  onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  className="input-organic"
                  placeholder="75.5"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                  Notas
                </label>
                <textarea
                  data-testid="measurement-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-organic resize-none"
                  rows={3}
                  placeholder="Observaciones del check-in..."
                />
              </div>
            </div>
          )}

          {activeSection === 'perimeters' && (
            <div className="grid grid-cols-2 gap-4 animate-fade-in">
              {perimeterFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                    {field.label} (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.perimeters[field.key] || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      perimeters: { ...formData.perimeters, [field.key]: e.target.value ? parseFloat(e.target.value) : null }
                    })}
                    className="input-organic"
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>
          )}

          {activeSection === 'skinfolds' && (
            <div className="grid grid-cols-2 gap-4 animate-fade-in">
              {skinfoldFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                    {field.label} (mm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.skinfolds[field.key] || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      skinfolds: { ...formData.skinfolds, [field.key]: e.target.value ? parseFloat(e.target.value) : null }
                    })}
                    className="input-organic"
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 rounded-xl bg-red-50 text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-6 border-t border-stone-100">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !formData.weight_kg}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
              data-testid="save-measurement-btn"
            >
              {loading ? <div className="spinner" /> : 'Guardar medición'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Consultations Tab
const ConsultationsTab = ({ client, consultations, onConsultationCreated }) => {
  const [showRecorder, setShowRecorder] = useState(false);

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-medium text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Consultas con IA de voz
        </h2>
        <button
          data-testid="new-consultation-btn"
          onClick={() => setShowRecorder(true)}
          className="btn-accent flex items-center gap-2"
        >
          <Mic size={18} />
          Nueva consulta
        </button>
      </div>

      {consultations.length === 0 ? (
        <div className="card-organic empty-state">
          <div className="w-16 h-16 rounded-full bg-[#C25934]/10 flex items-center justify-center mb-4">
            <Mic size={28} className="text-[#C25934]" />
          </div>
          <h4 className="text-lg font-medium text-stone-700 mb-2">Sin consultas</h4>
          <p className="text-stone-500 mb-6">Graba tu primera consulta y obtén un resumen con IA</p>
          <button onClick={() => setShowRecorder(true)} className="btn-accent">
            Iniciar consulta
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {consultations.map((consultation, index) => (
            <ConsultationCard key={consultation.id} consultation={consultation} index={index} />
          ))}
        </div>
      )}

      {showRecorder && (
        <VoiceRecorder
          client={client}
          onClose={() => setShowRecorder(false)}
          onComplete={(c) => {
            onConsultationCreated(c);
            setShowRecorder(false);
          }}
        />
      )}
    </div>
  );
};

// Consultation Card
const ConsultationCard = ({ consultation, index }) => {
  const [expanded, setExpanded] = useState(false);

  const moodColors = {
    positive: 'badge-positive',
    neutral: 'badge-neutral',
    concern: 'badge-concern',
  };

  const moodLabels = {
    positive: 'Positivo',
    neutral: 'Neutral',
    concern: 'Atención',
  };

  return (
    <div className={`card-organic animate-fade-in stagger-${index % 4 + 1}`}>
      <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-[#C25934]/10">
            <Mic size={20} className="text-[#C25934]" />
          </div>
          <div>
            <p className="font-medium text-stone-800">
              {new Date(consultation.session_date).toLocaleDateString('es-ES', { 
                weekday: 'long', day: 'numeric', month: 'long' 
              })}
            </p>
            <p className="text-sm text-stone-500 mt-1 line-clamp-2">
              {consultation.ai_summary || 'Sin resumen disponible'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {consultation.tokens_used > 0 && (
            <span className="vitamin-badge animate-vitamin">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <ellipse cx="12" cy="12" rx="10" ry="6" fill="currentColor" opacity="0.3"/>
                <ellipse cx="12" cy="12" rx="8" ry="4.5" fill="currentColor"/>
              </svg>
              -{(consultation.tokens_used * 100).toLocaleString()}
            </span>
          )}
          {consultation.mood && (
            <span className={moodColors[consultation.mood]}>
              {moodLabels[consultation.mood]}
            </span>
          )}
          {consultation.is_demo && (
            <span className="badge-neutral">Demo</span>
          )}
          <ChevronDown size={20} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
        </div>
      </div>

      {expanded && (
        <div className="mt-6 pt-6 border-t border-stone-100 space-y-4 animate-fade-in">
          {consultation.key_points?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Puntos clave</p>
              <ul className="space-y-2">
                {consultation.key_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6B8E7D] mt-2" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {consultation.plan_modifications?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Modificaciones al plan</p>
              <ul className="space-y-2">
                {consultation.plan_modifications.map((mod, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <Edit3 size={14} className="text-[#C25934] mt-0.5" />
                    {mod}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {consultation.action_items?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Acciones a seguir</p>
              <ul className="space-y-2">
                {consultation.action_items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <CheckCircle2 size={16} className="text-[#6B8E7D] mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Voice Recorder Component
const VoiceRecorder = ({ client, onClose, onComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [duration, setDuration] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [consultation, setConsultation] = useState(null);
  const [error, setError] = useState('');
  
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Check for Web Speech API support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'es-ES';

    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setTranscript(prev => prev + finalTranscript);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        // Restart recognition if no speech detected
        if (isRecording && !isPaused) {
          recognitionRef.current.start();
        }
      }
    };

    recognitionRef.current.onend = () => {
      if (isRecording && !isPaused) {
        recognitionRef.current.start();
      }
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused]);

  const startRecording = async () => {
    setError('');
    try {
      // Create consultation first
      const response = await consultationsAPI.create(client.id, {
        consultation_type: 'in_person'
      });
      setConsultation(response.data);
      
      setIsRecording(true);
      recognitionRef.current?.start();
      
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Error al iniciar la consulta');
      console.error(err);
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    recognitionRef.current?.stop();
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const analyzeTranscript = async () => {
    if (!consultation) return;
    
    setProcessing(true);
    setError('');
    
    try {
      const response = await consultationsAPI.analyze(client.id, consultation.id, {
        transcript_text: transcript,
        duration_seconds: duration
      });
      onComplete(response.data);
    } catch (err) {
      setError('Error al analizar la consulta. Inténtalo de nuevo.');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden animate-fade-in" data-testid="voice-recorder-modal">
        {/* AI Panel Header */}
        <div className="ai-panel border-b-0 rounded-b-none">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Consulta AI
              </h3>
              <p className="text-stone-500">Cliente: {client.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
              <X size={24} className="text-stone-500" />
            </button>
          </div>

          {/* Recording Controls */}
          <div className="flex flex-col items-center py-8">
            <div className="relative">
              <button
                data-testid="record-button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={processing}
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                  isRecording 
                    ? 'bg-[#C25934] recording-active' 
                    : 'bg-[#6B8E7D] hover:bg-[#5A7A6A]'
                }`}
              >
                {isRecording ? (
                  <Square size={32} className="text-white" fill="white" />
                ) : (
                  <Mic size={32} className="text-white" />
                )}
              </button>
            </div>

            <p className="text-4xl font-semibold text-stone-900 mt-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatDuration(duration)}
            </p>
            <p className="text-stone-500 mt-2">
              {isRecording ? 'Grabando...' : processing ? 'Analizando...' : 'Presiona para grabar'}
            </p>
          </div>
        </div>

        {/* Transcript Area */}
        <div className="p-6 bg-white">
          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">
              Transcripción
            </label>
          </div>
          <div className="bg-stone-50 rounded-xl p-4 min-h-[150px] max-h-[200px] overflow-y-auto">
            {transcript ? (
              <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">
                {transcript}
              </p>
            ) : (
              <p className="text-sm text-stone-400 italic">
                La transcripción aparecerá aquí mientras hablas...
              </p>
            )}
          </div>

          {error && (
            <div className="mt-4 p-4 rounded-xl bg-red-50 text-red-600 text-sm flex items-center gap-2">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="flex-1 btn-secondary">
              Cancelar
            </button>
            <button
              data-testid="analyze-button"
              onClick={analyzeTranscript}
              disabled={processing || isRecording || !transcript.trim()}
              className="flex-1 btn-accent flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <FileText size={18} />
                  Generar resumen
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Photos Tab
const PhotosTab = ({ client, measurements }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('front');
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPhotos();
  }, [client.id]);

  const loadPhotos = async () => {
    try {
      const response = await photosAPI.listAll(client.id);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedMeasurement) return;

    setUploading(true);
    try {
      await photosAPI.upload(client.id, selectedMeasurement, file, selectedCategory);
      await loadPhotos();
      setSelectedMeasurement(null);
      fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error al subir la foto. Máximo 5MB permitido.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (measurementId, photoId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await photosAPI.delete(client.id, measurementId, photoId);
      await loadPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const categoryLabels = {
    front: 'Frontal',
    side: 'Lateral',
    back: 'Espalda',
    other: 'Otra'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Upload Section */}
      <div className="card-organic mb-6">
        <h3 className="text-lg font-medium text-stone-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Subir nueva foto
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
              Medición
            </label>
            <select
              data-testid="photo-measurement-select"
              value={selectedMeasurement || ''}
              onChange={(e) => setSelectedMeasurement(e.target.value)}
              className="input-organic"
            >
              <option value="">Seleccionar medición...</option>
              {measurements.map((m) => (
                <option key={m.id} value={m.id}>
                  {new Date(m.measurement_date).toLocaleDateString('es-ES')} - {m.weight_kg}kg
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
              Categoría
            </label>
            <select
              data-testid="photo-category-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-organic"
            >
              <option value="front">Frontal</option>
              <option value="side">Lateral</option>
              <option value="back">Espalda</option>
              <option value="other">Otra</option>
            </select>
          </div>
          <div className="flex items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="photo-file-input"
            />
            <button
              data-testid="upload-photo-btn"
              onClick={() => selectedMeasurement && fileInputRef.current?.click()}
              disabled={!selectedMeasurement || uploading}
              className={`w-full btn-primary flex items-center justify-center gap-2 ${
                !selectedMeasurement ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Upload size={18} />
                  Subir foto
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Photos Gallery */}
      {photos.length === 0 ? (
        <div className="card-organic empty-state">
          <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
            <Camera size={28} className="text-stone-400" />
          </div>
          <h4 className="text-lg font-medium text-stone-700 mb-2">Sin fotos</h4>
          <p className="text-stone-500">Sube fotos de progreso para este cliente</p>
        </div>
      ) : (
        <div className="space-y-6">
          {photos.map((group, groupIndex) => (
            <div key={group.measurement_id} className={`card-organic animate-fade-in stagger-${groupIndex % 4 + 1}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-stone-800">
                    {new Date(group.measurement_date).toLocaleDateString('es-ES', { 
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </h4>
                  <p className="text-sm text-stone-500">{group.weight_kg} kg</p>
                </div>
                <span className="text-xs text-stone-400">{group.photos.length} fotos</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {group.photos.map((photo) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.photo_url}
                      alt={`Foto ${photo.category}`}
                      className="w-full h-48 object-cover rounded-xl cursor-pointer transition-transform hover:scale-[1.02]"
                      onClick={() => setLightboxPhoto(photo)}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                      <button
                        onClick={() => setLightboxPhoto(photo)}
                        className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors"
                      >
                        <Image size={18} className="text-stone-700" />
                      </button>
                      <button
                        onClick={() => handleDelete(group.measurement_id, photo.id)}
                        className="p-2 bg-red-500/90 rounded-lg hover:bg-red-500 transition-colors"
                      >
                        <Trash2 size={18} className="text-white" />
                      </button>
                    </div>
                    <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded-lg">
                      {categoryLabels[photo.category]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            onClick={() => setLightboxPhoto(null)}
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={lightboxPhoto.photo_url}
            alt="Foto ampliada"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
